/**
 * Recepción — vista principal.
 *
 * Estados:
 *   - tab=pendientes  → remitos que otros eslabones informaron a ANMAT hacia
 *                       este GLN (búsqueda, sin precarga).
 *   - click en una card → se abre (o retoma) el ACTA del remito y se muestra
 *                       `ActaRecepcionView`.
 *   - tab=completadas → actas cerradas o en confirmación; click abre el acta.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/notify';
import { ApiError } from '@/api/http';
import { useAbrirActaMutation } from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import { PendientesRecepcionList } from './PendientesRecepcionList';
import { CompletadasRecepcionTable } from './CompletadasRecepcionTable';
import { ActaRecepcionView } from './ActaRecepcionView';
import type { RecepcionRemito } from '@/api/contracts/recepcion-pendientes';
import type { NovedadesActa } from '@/api/contracts/recepcion-acta';

type Tab = 'pendientes' | 'completadas';

function textoNovedades(n: NovedadesActa): string | null {
  const partes: string[] = [];
  if (n.actaAnulada) return 'El emisor canceló en ANMAT todo lo que quedaba de este documento.';
  if (n.seriesNuevas)
    partes.push(
      `${n.seriesNuevas} serie${n.seriesNuevas === 1 ? '' : 's'} nueva${n.seriesNuevas === 1 ? '' : 's'}`,
    );
  if (n.anuladasPorEmisor)
    partes.push(
      `${n.anuladasPorEmisor} anulada${n.anuladasPorEmisor === 1 ? '' : 's'} por el emisor`,
    );
  if (n.confirmadasFuera)
    partes.push(
      `${n.confirmadasFuera} confirmada${n.confirmadasFuera === 1 ? '' : 's'} fuera del sistema`,
    );
  return partes.length > 0 ? `Novedades desde ANMAT: ${partes.join(', ')}.` : null;
}

export function RecepcionPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('pendientes');
  const [actaActivaId, setActaActivaId] = useState<number | null>(null);
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const abrir = useAbrirActaMutation();

  // Cambio de GLN → volvemos al listado, mismo patrón que despacho.
  const ubicacionPrevia = useRef(ubicacionActivaId);
  useEffect(() => {
    if (ubicacionPrevia.current !== ubicacionActivaId) {
      ubicacionPrevia.current = ubicacionActivaId;
      setActaActivaId(null);
    }
  }, [ubicacionActivaId]);

  // Al volver al listado, invalidamos para que refleje lo que pasó en el acta.
  const actaPrevia = useRef(actaActivaId);
  useEffect(() => {
    if (actaPrevia.current != null && actaActivaId == null) {
      void qc.invalidateQueries({ queryKey: ['recepcion-pendientes'] });
      void qc.invalidateQueries({ queryKey: ['recepcion-completadas'] });
    }
    actaPrevia.current = actaActivaId;
  }, [actaActivaId, qc]);

  const abrirRemito = (remito: RecepcionRemito) => {
    if (!ubicacionActivaId) {
      toast.error('No hay GLN activo. Elegí un GLN antes de abrir una recepción.');
      return;
    }
    if (remito.localTask?.id) {
      setActaActivaId(remito.localTask.id);
      return;
    }
    abrir.mutate(
      {
        glnId: ubicacionActivaId,
        numeroRemito: remito.numeroRemito,
        numeroFactura: remito.numeroRemito ? null : remito.numeroFactura,
        glnOrigen: remito.glnOrigen,
      },
      {
        onSuccess: ({ acta, novedades }) => {
          setActaActivaId(acta.id);
          const texto = textoNovedades(novedades);
          if (texto) toast.warning(texto);
        },
        onError: (err) => {
          const code = err instanceof ApiError ? err.code : null;
          toast.error(
            code === 'ACTA_EMISOR_AMBIGUO'
              ? 'Más de un emisor tiene pendiente ese documento y no se pudo distinguir. Buscalo por remito y elegí la tarjeta del emisor correcto.'
              : `No se pudo abrir el acta: ${err.message}`,
          );
        },
      },
    );
  };

  if (actaActivaId != null) {
    return <ActaRecepcionView actaId={actaActivaId} onVolver={() => setActaActivaId(null)} />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-everex-azul">Recepción</CardTitle>
          <p className="text-xs text-gray-600">
            Remitos que otros eslabones informaron a ANMAT hacia este GLN. Buscá uno, marcá lo que
            llegó (escaneando o a mano), confirmalo y finalizá el ingreso.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 border-b border-gray-200">
            <TabButton active={tab === 'pendientes'} onClick={() => setTab('pendientes')}>
              Pendientes
            </TabButton>
            <TabButton active={tab === 'completadas'} onClick={() => setTab('completadas')}>
              Realizadas
            </TabButton>
          </div>
        </CardContent>
      </Card>

      {abrir.isPending && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Abriendo el acta (consultando ANMAT)…
        </div>
      )}

      {tab === 'pendientes' ? (
        <PendientesRecepcionList onAbrirRemito={abrirRemito} abriendo={abrir.isPending} />
      ) : (
        <CompletadasRecepcionTable onAbrirActa={setActaActivaId} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm transition',
        active
          ? 'border-b-2 border-everex-naranja font-semibold text-everex-azul'
          : 'text-gray-600 hover:text-everex-azul',
      )}
    >
      {children}
    </button>
  );
}
